import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Upload, message } from 'antd';
import axios from 'axios';
import React, { useState } from 'react';

interface Props {
  /** 현재 저장된 logo URL (없으면 null/undefined). */
  value?: string | null;
  /** 업로드 대상 detail API URL (ex. `${BASE_URL}/api/v1/series/12/`). */
  endpoint: string;
  /** multipart field name (BE FileField 명) — default 'logo'. */
  fieldName?: string;
  /** 업로드/삭제 성공 시 새 URL (또는 null) 콜백. 부모가 state 갱신용. */
  onChange?: (newUrl: string | null) => void;
  /** UI 라벨. */
  label?: string;
}

/**
 * BEE-5022 (PRD v1.4 #40): Series.logo / Season.logo 업로드용 inline 위젯.
 * 별도 Form 안에 들어가지 않고 즉시 PATCH 로 multipart 전송 — 본 페이지의
 * 메인 form save 와 분리되어 있어 사진만 따로 갱신 가능.
 */
const LogoUploadField: React.FC<Props> = ({
  value,
  endpoint,
  fieldName = 'logo',
  onChange,
  label = 'Logo',
}) => {
  const [uploading, setUploading] = useState(false);

  const upload = async (file: File) => {
    setUploading(true);
    const form = new FormData();
    form.append(fieldName, file);
    try {
      const resp = await axios.patch(endpoint, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newUrl: string | null =
        resp?.data?.[fieldName] ?? resp?.data?.logo ?? null;
      onChange?.(newUrl);
      message.success(`${label} 업로드됨`);
    } catch (e: any) {
      const detail =
        e?.response?.data?.detail ||
        JSON.stringify(e?.response?.data) ||
        e?.message ||
        'unknown';
      message.error(`${label} 업로드 실패: ${detail}`);
    } finally {
      setUploading(false);
    }
  };

  const clear = async () => {
    setUploading(true);
    const form = new FormData();
    // BE FileField clear pattern — empty value (null serialized as "")
    form.append(fieldName, '');
    try {
      await axios.patch(endpoint, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange?.(null);
      message.success(`${label} 제거됨`);
    } catch (e: any) {
      const detail =
        e?.response?.data?.detail || e?.message || 'unknown';
      message.error(`${label} 제거 실패: ${detail}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>
        {label}
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        {value ? (
          <img
            src={value}
            alt={label}
            style={{
              maxHeight: 80,
              background: '#222',
              padding: 8,
              borderRadius: 4,
            }}
          />
        ) : (
          <span style={{ color: '#888' }}>(no logo)</span>
        )}
        <Upload
          accept="image/*"
          showUploadList={false}
          beforeUpload={file => {
            upload(file);
            return false; // antd 가 자체 업로드 안 하게.
          }}
        >
          <Button icon={<UploadOutlined />} loading={uploading}>
            {value ? 'Replace' : 'Upload'}
          </Button>
        </Upload>
        {value ? (
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={clear}
            loading={uploading}
          >
            Remove
          </Button>
        ) : null}
      </div>
      <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
        Shorts player 의 parent_program_logo 로 자동 fallback (Season.logo →
        Series.logo → 없으면 텍스트 타이틀).
      </div>
    </div>
  );
};

export default LogoUploadField;
